import { useGoogleLogin } from '@react-oauth/google'
import { Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import type { GoogleSignInButtonProps } from '@/components/auth/GoogleSignInButton.types'
import { Button } from '@/components/ui/button'
import { useNavigationManager } from '@/lib/navigation'
import { useAuthStore } from '@/stores/auth'

export function GoogleSignInButton({
  label = 'Continue with Google',
  onRegistrationSuccess,
}: GoogleSignInButtonProps) {
  const { googleLogin, clearError } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const navigationManager = useNavigationManager(navigate, location)
  const returnTo = searchParams.get('returnTo')
  const [isLoading, setIsLoading] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)

  const login = useGoogleLogin({
    flow: 'implicit',
    onSuccess: async (tokenResponse) => {
      setLocalError(null)
      try {
        if (onRegistrationSuccess) {
          await onRegistrationSuccess(tokenResponse.access_token)
          return
        }

        await googleLogin(tokenResponse.access_token)
        const { user } = useAuthStore.getState()

        navigationManager.redirectAfterLogin(user ?? undefined, returnTo)
      } catch (err) {
        setLocalError(
          err instanceof Error
            ? err.message
            : 'Google Sign-In failed. Please try again.'
        )
      } finally {
        setIsLoading(false)
      }
    },
    onError: () => {
      setIsLoading(false)
      setLocalError('Google Sign-In was cancelled or failed. Please try again.')
    },
    onNonOAuthError: () => {
      setIsLoading(false)
      setLocalError(
        'Google Sign-In popup was blocked or closed. Please try again.'
      )
    },
  })

  const handleClick = () => {
    clearError()
    setLocalError(null)
    setIsLoading(true)
    login()
  }

  return (
    <div className="space-y-2">
      <Button
        type="button"
        variant="outline"
        className="w-full h-9 rounded-full border-border bg-background px-3 py-1 text-[14px] shadow-sm flex items-center justify-center gap-2"
        disabled={isLoading}
        onClick={handleClick}
      >
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <GoogleIcon />
        )}
        {isLoading ? 'Connecting...' : label}
      </Button>

      {localError && (
        <p className="text-[13px] text-destructive text-center">{localError}</p>
      )}
    </div>
  )
}

function GoogleIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M17.64 9.2045c0-.6381-.0573-1.2518-.1636-1.8409H9v3.4814h4.8436c-.2086 1.125-.8427 2.0782-1.7959 2.7164v2.2581h2.9087c1.7018-1.5668 2.6836-3.874 2.6836-6.615Z"
        fill="#4285F4"
      />
      <path
        d="M9 18c2.43 0 4.4673-.806 5.9564-2.1805l-2.9087-2.2581c-.8055.54-1.8368.859-3.0477.859-2.3441 0-4.3282-1.5831-5.036-3.7104H.9574v2.3318C2.4382 15.9832 5.4818 18 9 18Z"
        fill="#34A853"
      />
      <path
        d="M3.964 10.71c-.18-.54-.2822-1.1168-.2822-1.71s.1023-1.17.2822-1.71V4.9582H.9574C.3477 6.1731 0 7.5477 0 9s.3477 2.8268.9574 4.0418L3.964 10.71Z"
        fill="#FBBC05"
      />
      <path
        d="M9 3.5795c1.3214 0 2.5077.4541 3.4405 1.346l2.5813-2.5814C13.4627.8918 11.4255 0 9 0 5.4818 0 2.4382 2.0168.9574 4.9582L3.964 7.29C4.6718 5.1627 6.6559 3.5795 9 3.5795Z"
        fill="#EA4335"
      />
    </svg>
  )
}
